import { useCallback } from 'react';
import { useNotification } from '@/common/components/notification/NotificationContext';
import {
  useDeleteProjectMutation,
  useGetProjectsQuery,
} from '@/generated/graphql';

interface UseProjectDeletionProps {
  projectId: string;
  onClose?: () => void;
}

/**
 * Hook to manage project deletion
 * Encapsulates deletion logic and confirmation
 */
export function useProjectDeletion({
  projectId,
  onClose,
}: UseProjectDeletionProps) {
  const { showSuccess, showError } = useNotification();
  const [deleteProject, { loading: deleteLoading }] =
    useDeleteProjectMutation();
  const { refetch: refetchProjects } = useGetProjectsQuery({
    fetchPolicy: 'network-only',
  });

  const handleDeleteProject = useCallback(async () => {
    if (
      !confirm(
        'Are you sure you want to delete this project? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await deleteProject({
        variables: { projectId },
      });
      showSuccess('Project deleted successfully!');

      await refetchProjects();
      onClose?.();
    } catch (error) {
      console.error('Error deleting project:', error);
      showError('Failed to delete project. Please try again.');
    }
  }, [
    projectId,
    deleteProject,
    refetchProjects,
    onClose,
    showSuccess,
    showError,
  ]);

  return {
    handleDeleteProject,
    deleteLoading,
  };
}
